import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/Tune';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { ACCENT_COLOR, SURFACE_BG } from '@/constants/colors';
import { FEEDBACK_EMAILS } from '@/constants/app';
import { useUIStore } from '@/stores/useUIStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { useInputStore } from '@/stores/useInputStore';
import { useConfigStore } from '@/stores/useConfigStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useJobStore } from '@/stores/useJobStore';
import { useSessionStore } from '@/stores/useSessionStore';
import { geometryKeys } from '@/hooks/useGeometry';
import type { StarRating } from '@/types/sample';

/**
 * RibbonBar — Thin header row above the three main columns.
 *
 * Left side  (col 1): "Inputs" label with tune icon + info button
 * Right side (col 2–3): "Output" label with scatter-plot icon + 5-star rating + feedback
 *
 * Grid placement is handled by the parent AppShell; this component
 * renders both halves as sibling fragments.
 */

const STAR_COUNT = 5;

const FEEDBACK_HREF = `https://outlook.office.com/mail/deeplink/compose?to=${FEEDBACK_EMAILS.map(encodeURIComponent).join('%3B')}&subject=Optibush%20Feedback`;

const RibbonSection: React.FC<{
  icon: React.ReactNode;
  label: string;
  gridColumn: string | Record<string, string>;
  gridRow?: string | Record<string, string>;
  children?: React.ReactNode;
}> = ({ icon, label, gridColumn, gridRow = '1', children }) => (
  <Box
    sx={{
      gridColumn,
      gridRow,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1,
      borderRadius: 1,
      bgcolor: SURFACE_BG,
      boxShadow: 1,
      overflow: 'hidden',
    }}
  >
    {icon}
    <Typography
      variant="body2"
      sx={{ fontWeight: 700, color: ACCENT_COLOR, whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>

    {/* Spacer */}
    <Box sx={{ flex: 1 }} />

    {children}
  </Box>
);

const RibbonBar: React.FC = () => {
  const openDialog = useUIStore((s) => s.openDialog);
  const rating = useSampleStore((s) => s.currentRating);
  const setRating = useSampleStore((s) => s.setRating);
  const queryClient = useQueryClient();

  const handleStar = useCallback((index: number) => {
    setRating((rating === index ? 0 : index) as StarRating);
  }, [rating, setRating]);

  const handleReset = useCallback(() => {
      // Reset every store back to its first-load default. Imperative
      // getState() calls (not hook subscriptions) since this is a
      // one-shot action, not something RibbonBar needs to re-render on.

      // Switch back to A1 first — this is what triggers the existing
      // training-data fetch for the new bushing type (same effect that
      // runs on any normal bushing-type switch), repopulating
      // originalSamples for A1's scatter plot.
      useBushingStore.getState().setBushingType('A1');

      useInputStore.getState().resetInputs();
      useConfigStore.getState().resetConfig();

      // Only clear generated samples/selection — NOT originalSamples.
      // clearSamples() would wipe training data too, leaving the scatter
      // plot blank until/unless something else refetches it.
      useSampleStore.getState().clearGeneratedSamples();

      useJobStore.getState().resetJobs();
      useUIStore.getState().resetUI();
      queryClient.removeQueries({ queryKey: geometryKeys.all });

      // Session persists across resets (it's the analytics session, not
      // app state) — just log that a reset happened.
      useSessionStore.getState().trackEvent('reset');
    }, []);

  return (
    <>
      {/* ── Inputs ribbon (Column 1) ──────────────────────── */}
      <RibbonSection
        icon={<TuneIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />}
        label="Inputs"
        gridColumn={{ xs: '1', lg: '1' }}
        gridRow={{ xs: 'auto', lg: '1' }}
      >
        {/* Info button */}
        <Tooltip title="App info">
          <IconButton
            size="small"
            onClick={() => openDialog('infoDialog')}
            sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#115293' }, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
            aria-label="App info"
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        {/* Reset button */}
        <Tooltip title="Reset all">
          <IconButton
            size="small"
            onClick={handleReset}
            sx={{ bgcolor: '#ef5350', color: '#fff', '&:hover': { bgcolor: '#b71c1c' }, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
            aria-label="Reset all"
          >
            <RestoreIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </RibbonSection>

      {/* ── Output ribbon (Columns 2–3) ───────────────────── */}
      <RibbonSection
        icon={<ScatterPlotIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />}
        label="Output"
        gridColumn={{ xs: '1', lg: '2 / span 2' }}
        gridRow={{ xs: 'auto', lg: '1' }}
      >
        {/* 5-star rating */}
        <Tooltip title="Rate the selected sample">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {Array.from({ length: STAR_COUNT }, (_, i) => {
              const starIndex = i + 1;
              return (
                <IconButton
                  key={starIndex}
                  size="small"
                  onClick={() => handleStar(starIndex)}
                  aria-label={`Rate ${starIndex} star${starIndex > 1 ? 's' : ''}`}
                  sx={{ p: 0 }}
                >
                  {starIndex <= rating ? (
                    <StarIcon sx={{ fontSize: 24, color: '#faaf00' }} />
                  ) : (
                    <StarBorderIcon sx={{ fontSize: 24, color: '#868484' }} />
                  )}
                </IconButton>
              );
            })}
          </Box>
        </Tooltip>
        <Tooltip title="Send Feedback">
          <IconButton
            component="a"
            href={FEEDBACK_HREF}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label="Send Feedback"
          >
            <EmailOutlinedIcon sx={{ fontSize: 18, color: '#1976d2' }} />
          </IconButton>
        </Tooltip>
      </RibbonSection>
    </>
  );
};

export default RibbonBar;